import { IMeeting } from "@mctypes/meeting.types";
import { IExternalParticipant, IMeetingAgendaByMailPostBody } from "@schemas/meetingSchema";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { ToastMessagesEnums } from "../../../mc-shared/sharedConstants";
import { trpcSlice } from "../../../trpcSlice";

export const meetingRTK = trpcSlice.injectEndpoints({
  endpoints: (builder) => ({
    updateMeeting: builder.mutation<
      IMeeting.Meeting,
      { projectId: string; meetingId: string; attribute: string; value: string | boolean | Date }
    >({
      query: (input) => ({
        url: `/project/${input.projectId}/meeting/${input.meetingId}/attribute`,
        method: "PUT",
        body: {
          attribute: input.attribute,
          value: input.value,
        },
      }),
      async onQueryStarted({ meetingId }, { dispatch, queryFulfilled, getState }) {
        await queryFulfilled;
        dispatch(setShowSuccessMessageThunk("Møtet er oppdatert"));
        dispatch({ type: "meeting/refreshMeetingList" });
      },
      invalidatesTags: ["Meeting"],
    }),
    getMeetingInstancesWhereUserIsAParticipant: builder.query<
      IMeeting.Meeting[],
      { userId: string; projectId: string }
    >({
      query: (input) => ({
        url: `/project/${input.projectId}/meeting/participant/${input.userId}`,
        method: "GET",
      }),
      providesTags: ["Meeting"],
    }),
    createMeetingInstance: builder.mutation<IMeeting.Meeting, { projectId: string; startDate: Date; title: string }>({
      query(input) {
        return {
          url: `/project/${input.projectId}/meeting/new-single-meeting`,
          method: "POST",
          body: {
            startDate: input.startDate,
            title: input.title,
          },
        };
      },
      async onQueryStarted({}, { dispatch, queryFulfilled, getState }) {
        await queryFulfilled;
        dispatch(setShowSuccessMessageThunk("Møtet er lagret"));
        dispatch({ type: "meeting/refreshMeetingList" });
      },
      invalidatesTags: ["Meeting"],
    }),
    copyMeetingInstance: builder.mutation<
      IMeeting.Meeting,
      { projectId: string; meetingSerieId: string; dateOfMeeting: Date; copyAgenda: boolean }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/meeting/new-meeting-in-series`,
          method: "PUT",
          body: {
            dateOfMeeting: input.dateOfMeeting,
            copyAgenda: input.copyAgenda,
            meetingSerieId: input.meetingSerieId,
          },
        };
      },
      async onQueryStarted({}, { dispatch, queryFulfilled, getState }) {
        await queryFulfilled;
        dispatch(setShowSuccessMessageThunk("Møtet er kopiert"));
        dispatch({ type: "meeting/refreshMeetingList" });
      },
      invalidatesTags: ["Meeting"],
    }),
    sendMeetingAgendaByMail: builder.mutation<
      string,
      {
        body: IMeetingAgendaByMailPostBody;
        params: { projectId: string; meetingId: string };
      }
    >({
      query: (input) => ({
        url: `/project/${input.params.projectId}/meeting/${input.params.meetingId}/sendagenda`,
        method: "PUT",
        body: input.body,
      }),
      async onQueryStarted({}, { dispatch, queryFulfilled, getState }) {
        dispatch(setShowSuccessMessageThunk("Agenda er sendt"));
      },
      invalidatesTags: ["Meeting"],
    }),
    addMeetingParticipant: builder.mutation<
      IMeeting.Meeting,
      {
        projectId: string;
        meetingId: string;
        userId: string;
      }
    >({
      query: (input) => ({
        url: `/project/${input.projectId}/meeting/${input.meetingId}/participant/${input.userId}`,
        method: "POST",
        body: {},
      }),
      async onQueryStarted({}, { dispatch, queryFulfilled, getState }) {
        dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
      },
      invalidatesTags: ["Meeting"],
    }),
    updateMeetingParticipant: builder.mutation<
      IMeeting.Meeting,
      {
        projectId: string;
        meetingId: string;
        participantId: string;
        attribute: string;
        value: string | boolean | Date;
      }
    >({
      query: (input) => ({
        url: `/project/${input.projectId}/meeting/${input.meetingId}/participant/${input.participantId}`,
        method: "PUT",
        body: {
          attribute: input.attribute,
          value: input.value,
        },
      }),
      async onQueryStarted({}, { dispatch, queryFulfilled, getState }) {
        dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
      },
      invalidatesTags: ["Meeting"],
    }),
    removeMeetingParticipant: builder.mutation<
      IMeeting.Meeting,
      {
        projectId: string;
        meetingId: string;
        participantId: string;
      }
    >({
      query: (input) => ({
        url: `/project/${input.projectId}/meeting/${input.meetingId}/participant/${input.participantId}`,
        method: "DELETE",
      }),
      async onQueryStarted({}, { dispatch, queryFulfilled, getState }) {
        dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
      },
      invalidatesTags: ["Meeting"],
    }),
    updateExternalParticipants: builder.mutation<
      IMeeting.Meeting,
      {
        projectId: string;
        meetingId: string;
        externalParticipants: IExternalParticipant[];
      }
    >({
      query: (input) => ({
        url: `/project/${input.projectId}/meeting/${input.meetingId}/externalParticipants`,
        body: { externalParticipants: input.externalParticipants },
        method: "POST",
      }),
      async onQueryStarted({}, { dispatch, queryFulfilled, getState }) {
        dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
      },
      invalidatesTags: ["Meeting"],
    }),
    updateAllAgendaIds: builder.mutation<
      IMeeting.Meeting,
      {
        projectId: string;
        meetingId: string;
      }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/meeting/${input.meetingId}/updateAgendaIds`,
          method: "PUT",
          body: {},
        };
      },
      async onQueryStarted({}, { dispatch, queryFulfilled, getState }) {
        dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
      },
      invalidatesTags: ["Meeting"],
    }),
  }),
});

export const {
  useUpdateMeetingMutation,
  useGetMeetingInstancesWhereUserIsAParticipantQuery,
  useCreateMeetingInstanceMutation,
  useCopyMeetingInstanceMutation,
  useSendMeetingAgendaByMailMutation,
  useAddMeetingParticipantMutation,
  useUpdateMeetingParticipantMutation,
  useRemoveMeetingParticipantMutation,
  useUpdateExternalParticipantsMutation,
  useUpdateAllAgendaIdsMutation,
} = meetingRTK;
